import { useState, useEffect } from 'react';
import SectionTitle from '../../../components/atoms/SectionTitle';
import SectionDesc from '../../../components/atoms/SectionDesc';
import Input from '../../../components/atoms/Input';
import Button from '../../../components/atoms/Button';
import CustomModal from '../../../components/atoms/CustomModal';
import TitleText from '../../../components/atoms/TitleText';
import { CloseSquare } from 'iconsax-react';
import { useTranslation } from 'react-i18next';
import OffersSection from './OffersSection';
import { message } from 'antd';
import { CoinsPlan } from '../../../types/coins';
import {
  useGetCoinsOffersQuery,
  useCreateCoinsOfferMutation,
  useEditCoinsOfferMutation,
  useDeleteCoinsOfferMutation,
  useToggleCoinsOfferActiveMutation,
} from '../../../store/services/coinsControl';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';

export default function CoinsOffers() {
  const { t } = useTranslation();
  const [modalType, setModalType] = useState<'add' | 'edit' | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState<CoinsPlan | null>(null);

  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const schema = z.object({
    offerNameEn: z
      .string()
      .regex(
        /^[a-zA-Z0-9\s!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]*$/,
        t('categories.validation.englishRegex') as string
      )
      .min(5, t('components.validations.minLength', { value: 5 }) as string)
      .max(50, t('components.validations.maxLength', { value: 50 }) as string),
    offerNameAr: z
      .string()
      .regex(
        /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF0-9\s!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]*$/,
        t('categories.validation.arabicRegex') as string
      )
      .min(5, t('components.validations.minLength', { value: 5 }) as string)
      .max(50, t('components.validations.maxLength', { value: 50 }) as string),
    coinsAmount: z.coerce
      .number({
        invalid_type_error: t('components.validations.requiredField') as string,
      })
      .gt(0, t('components.validations.requiredField') as string),
    eqPrice: z.coerce
      .number({
        invalid_type_error: t('components.validations.requiredField') as string,
      })
      .gt(0, t('components.validations.requiredField') as string),
    discount: z.preprocess(
      (val) => (val === '' || val === undefined ? undefined : Number(val)),
      z
        .number({
          invalid_type_error: t(
            'components.validations.requiredField'
          ) as string,
        })
        .min(0, t('components.validations.requiredField') as string)
        .max(
          100,
          t('components.validations.maxLength', { value: 100 }) as string
        )
        .optional()
    ),
  });

  type FormData = z.infer<typeof schema>;

  const { control, handleSubmit, reset } = useForm<FormData>({
    mode: 'all',
    resolver: zodResolver(schema),
    defaultValues: {
      offerNameEn: '',
      offerNameAr: '',
      coinsAmount: undefined,
      eqPrice: undefined,
      discount: undefined,
    },
  });

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 500);
    return () => clearTimeout(timer);
  }, [search]);

  // ProfileType for Supplier Admin

  const { data: offersData, isLoading } = useGetCoinsOffersQuery(
    {
      page: page + 1,
      limit: 10,
      search: debouncedSearch,
    },
    { refetchOnMountOrArgChange: true }
  );

  const [createOffer, { isLoading: isCreating }] =
    useCreateCoinsOfferMutation();
  const [editOffer, { isLoading: isEditing }] = useEditCoinsOfferMutation();
  const [deleteOffer, { isLoading: isDeleting }] =
    useDeleteCoinsOfferMutation();
  const [toggleActive] = useToggleCoinsOfferActiveMutation();

  const handleActivate = async (item: CoinsPlan, active: boolean) => {
    try {
      await toggleActive({ id: item.id, active }).unwrap();
      message.success(t('common.success', 'Updated successfully'));
    } catch (error: any) {
      const errData = error?.data;
      const errorDetail = errData?.errors
        ? (Object.values(errData.errors)[0] as string)
        : null;
      message.error(
        errorDetail ||
          errData?.message ||
          t('common.error', 'Something went wrong')
      );
    }
  };

  const handleOpenEdit = (item: CoinsPlan) => {
    setSelectedOffer(item);
    reset({
      offerNameEn: item.nameEn || item.name || '',
      offerNameAr: item.nameAr || item.name || '',
      coinsAmount: item.coinsNo,
      eqPrice: item.price,
      discount: item.discount,
    });
    setModalType('edit');
  };

  const handleOpenDelete = (item: CoinsPlan) => {
    setSelectedOffer(item);
    setIsDeleteModalOpen(true);
  };

  const onSubmit = async (data: FormData) => {
    try {
      if (modalType === 'add') {
        await createOffer({
          nameEn: data.offerNameEn,
          nameAr: data.offerNameAr,
          coinsNo: data.coinsAmount,
          price: data.eqPrice,
          discount: data.discount ?? 0,
        }).unwrap();
        message.success(t('common.success', 'Added successfully'));
      } else if (modalType === 'edit' && selectedOffer) {
        await editOffer({
          id: selectedOffer.id,
          body: {
            nameEn: data.offerNameEn,
            nameAr: data.offerNameAr,
            coinsNo: data.coinsAmount,
            price: data.eqPrice,
            discount: data.discount ?? 0,
          },
        }).unwrap();
        message.success(t('common.success', 'Updated successfully'));
      }
      setModalType(null);
      reset();
      setSelectedOffer(null);
    } catch (error: any) {
      const errData = error?.data;
      const errorDetail = errData?.errors
        ? (Object.values(errData.errors)[0] as string)
        : null;
      message.error(
        errorDetail ||
          errData?.message ||
          t('common.error', 'Something went wrong')
      );
    }
  };

  const handleDelete = async () => {
    if (!selectedOffer) return;
    try {
      await deleteOffer(selectedOffer.id).unwrap();
      message.success(t('common.success', 'Deleted successfully'));
      setIsDeleteModalOpen(false);
      setSelectedOffer(null);
    } catch (error: any) {
      const errData = error?.data;
      const errorDetail = errData?.errors
        ? (Object.values(errData.errors)[0] as string)
        : null;
      message.error(
        errorDetail ||
          errData?.message ||
          t('common.error', 'Something went wrong')
      );
    }
  };

  const isSaving = isCreating || isEditing;

  return (
    <div className="flex flex-col gap-6">
      {/* Page Header */}
      <div className="flex flex-col gap-1">
        <SectionTitle text={t('home.coins-offers-title')} />
        <SectionDesc text={t('home.coins-offers-page-desc')} />
      </div>

      {/* Offers Table Section */}
      <OffersSection
        data={offersData?.payload?.content || []}
        isLoading={isLoading}
        totalElements={offersData?.payload?.totalElements || 0}
        currentPage={page}
        onPageChange={(p) => setPage(p - 1)}
        onSearch={(val) => {
          setSearch(val);
          setPage(0);
        }}
        onAdd={() => {
          reset({
            offerNameEn: '',
            offerNameAr: '',
            coinsAmount: undefined,
            eqPrice: undefined,
            discount: undefined,
          });
          setModalType('add');
        }}
        onEdit={handleOpenEdit}
        onDelete={handleOpenDelete}
        onActivate={handleActivate}
      />

      {/* Add/Edit Modal */}
      <CustomModal
        isOpen={!!modalType}
        onClose={() => setModalType(null)}
        title={
          modalType === 'add'
            ? t('home.add-coins-offer')
            : t('home.edit-coins-offer')
        }
        subTitle={t('home.apply-new-coin-offer')}
        width={700}
        closeIconClassName="[&_.ant-modal-close]:!top-8"
      >
        <div className="flex flex-col gap-5 mt-4">
          <Controller
            name="offerNameEn"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Input
                id="offerNameEn"
                label={t('home.offer-name-en')}
                placeholder={t('home.set-name')}
                {...field}
                status={error ? 'error' : undefined}
                helperText={error?.message}
                className="!rounded-xl"
              />
            )}
          />
          <Controller
            name="offerNameAr"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Input
                id="offerNameAr"
                label={t('home.offer-name-ar')}
                placeholder={t('home.set-name')}
                {...field}
                status={error ? 'error' : undefined}
                helperText={error?.message}
                className="!rounded-xl"
              />
            )}
          />

          <div className="flex flex-col sm:flex-row items-start gap-4 w-full">
            <div className="flex-1 w-full">
              <Controller
                name="coinsAmount"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Input
                    id="coinsAmount"
                    label={t('home.number-of-coins')}
                    placeholder={t('home.set-number')}
                    type="number"
                    {...field}
                    status={error ? 'error' : undefined}
                    helperText={error?.message}
                    className="!rounded-xl"
                  />
                )}
              />
            </div>
            <div className="flex-1 w-full">
              <Controller
                name="eqPrice"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Input
                    id="eqPrice"
                    label={t('home.equivalent-price-sar')}
                    placeholder={t('home.set-number')}
                    type="number"
                    {...field}
                    status={error ? 'error' : undefined}
                    helperText={error?.message}
                    className="!rounded-xl"
                  />
                )}
              />
            </div>
          </div>

          <Controller
            name="discount"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Input
                id="discount"
                label={t('home.discount')}
                optional
                placeholder={t('home.set-number')}
                type="number"
                {...field}
                status={error ? 'error' : undefined}
                helperText={error?.message}
                className="!rounded-xl"
              />
            )}
          />

          <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 mt-4 w-full">
            <Button
              variant="outline"
              onClick={() => {
                setModalType(null);
                reset();
              }}
              className="w-full sm:w-auto rounded-xl px-6 border border-gray-300 !bg-white !text-gray-700 hover:!bg-gray-50"
              disabled={isSaving}
            >
              {t('home.cancel')}
            </Button>
            <Button
              variant="dark"
              onClick={handleSubmit(onSubmit)}
              className="w-full sm:w-auto rounded-xl px-6"
              loading={isSaving}
            >
              {t('home.save-offer')}
            </Button>
          </div>
        </div>
      </CustomModal>

      {/* Delete Confirmation Modal */}
      <CustomModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title={null}
        centered
        width={715}
        closable={false}
      >
        <div className="flex flex-col justify-center text-center gap-2 py-4">
          <div className="w-12 h-12 rounded-[14px] bg-[#FEF2F2] flex items-center justify-center self-center mb-2">
            <CloseSquare
              size={24}
              className="text-[#EF4444]"
              variant="Outline"
            />
          </div>

          <TitleText
            text={t('home.delete-item-title')}
            size="2xl"
            className="!font-bold !text-[#111928]"
          />

          <p className="text-gray-500 text-sm max-w-[420px] mx-auto leading-relaxed">
            {t('home.delete-item-desc')}
          </p>

          <div className="flex flex-col-reverse sm:flex-row items-center justify-center sm:justify-end gap-3 mt-6 w-full">
            <Button
              variant="outline"
              onClick={() => setIsDeleteModalOpen(false)}
              className="w-full sm:w-auto rounded-xl px-8 py-2 font-semibold border border-gray-300 !bg-white !text-[#111928] hover:!bg-gray-50"
              disabled={isDeleting}
            >
              {t('home.cancel')}
            </Button>
            <Button
              variant="dark"
              onClick={handleDelete}
              className="w-full sm:w-auto rounded-xl px-12 py-2 font-semibold !bg-[#D92D20] hover:!bg-[#B91C1C] !text-white !border-[#D92D20]"
              loading={isDeleting}
            >
              {t('home.yes-delete')}
            </Button>
          </div>
        </div>
      </CustomModal>
    </div>
  );
}
